import { z } from "zod";
import { InvoiceSchema, InvoiceClassificationSchema } from "./invoice.js";

export const UnpaidInvoiceSummarySchema = z.object({
  invoice_number: z.string(),
  gross_amount: z.string(),
  issue_date: z.string(),
  due_date: z.string().nullable(),
  days_overdue: z.number().int().nullable(),
  // Feature 018: classification of the invoice; optional for backwards compat in tests
  classification: InvoiceClassificationSchema.optional(),
});
export type UnpaidInvoiceSummary = z.infer<typeof UnpaidInvoiceSummarySchema>;

export const ClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email().nullable(),
  nip: z.string().nullable(),
  debt_collection_enabled: z.boolean(),
  excluded_at: z.string().datetime({ offset: true }).nullable(),
  excluded_by_name: z.string().nullable(),
  unpaid_invoice_count: z.number().int().min(0),
  oldest_overdue_days: z.number().int().min(0).nullable(),
  // Invoice data for all tabs
  oldest_issue_date: z.string().nullable().optional(),
  unpaid_invoices: z.array(UnpaidInvoiceSummarySchema).optional(),
  // Manual exclusion flags
  external_collection: z.boolean().optional(),
  // Auto-exclusion fields (present only for auto_excluded tab)
  auto_excluded: z.boolean().optional(),
  auto_excluded_at: z.string().datetime({ offset: true }).nullable().optional(),
  auto_excluded_invoice_number: z.string().nullable().optional(),
  auto_excluded_invoice_amount: z.string().nullable().optional(),
  auto_excluded_invoice_payment_date: z.string().nullable().optional(),
  auto_excluded_invoice_confidence: z.string().nullable().optional(),
});
export type Client = z.infer<typeof ClientSchema>;

export const ClientDetailSchema = ClientSchema.omit({
  unpaid_invoice_count: true,
  oldest_overdue_days: true,
}).extend({
  invoices: z.array(InvoiceSchema),
});

export const ClientCollectionToggleRequestSchema = z.union([
  z.object({ enabled: z.boolean(), reason: z.string().optional(), external_collection: z.boolean().optional() }),
  z.object({ action: z.enum(["confirm_auto_exclude", "reenable"]), reason: z.string().optional() }),
]);
export type ClientCollectionToggleRequest = z.infer<typeof ClientCollectionToggleRequestSchema>;

export const CollectionStateLogEntrySchema = z.object({
  id: z.string().uuid(),
  client_id: z.string().uuid(),
  client_name: z.string(),
  changed_by_name: z.string().nullable(),
  changed_at: z.string().datetime({ offset: true }),
  from_enabled: z.boolean().nullable(),
  to_enabled: z.boolean(),
  reason: z.string().nullable(),
  change_type: z.enum(["manual", "confirm_auto_exclude", "reenable"]),
});
export type CollectionStateLogEntry = z.infer<typeof CollectionStateLogEntrySchema>;

export const CollectionLogResponseSchema = z.object({
  entries: z.array(CollectionStateLogEntrySchema),
  total: z.number().int().min(0),
});
export type CollectionLogResponse = z.infer<typeof CollectionLogResponseSchema>;

export const ClientCollectionToggleResponseSchema = z.object({
  id: z.string().uuid(),
  debt_collection_enabled: z.boolean(),
  auto_excluded: z.boolean(),
  excluded_at: z.string().datetime({ offset: true }).nullable(),
  excluded_by_name: z.string().nullable(),
  external_collection: z.boolean(),
});
export type ClientCollectionToggleResponse = z.infer<typeof ClientCollectionToggleResponseSchema>;

export const ClientCollectionFilterSchema = z.enum(["active", "excluded", "auto_excluded", "all"]);
export type ClientCollectionFilter = z.infer<typeof ClientCollectionFilterSchema>;

export const ClientListResponseSchema = z.object({
  clients: z.array(ClientSchema),
  total: z.number().int().min(0),
  auto_excluded_count: z.number().int().min(0).optional(),
});
export type ClientListResponse = z.infer<typeof ClientListResponseSchema>;
